import { Decision, DecisionLevel } from "../models/decision";
import { Policy } from "../models/policy";

export type Lang = "es" | "en" | "ja";

/**
 * Maps the Outlook display language (e.g. es-MX, ja-JP) to a supported message language; anything else is English.
 */
export function langFromDisplayLanguage(displayLanguage: string | undefined): Lang {
  const tag = (displayLanguage ?? "").toLowerCase();
  if (tag.startsWith("es")) return "es";
  if (tag.startsWith("ja")) return "ja";
  return "en";
}

export interface BuiltMessage {
  plain: string;
  markdown: string;
  cancelLabel: string;
}

/** Smart Alerts dialog messages are limited to 500 characters. */
const MAX_DIALOG_CHARS = 500;
const BULLET_LIMITS = [5, 3, 1];

const MORE: Record<Lang, (n: number) => string> = {
  en: (n) => `and ${n} more`,
  es: (n) => `y ${n} más`,
  ja: (n) => `ほか${n}件`,
};

function formatOrgList(orgLabels: string[], lang: Lang): string {
  const shown = orgLabels.slice(0, 5);
  const list = shown.join(", ");
  const remaining = orgLabels.length - shown.length;
  return remaining > 0 ? `${list}, ${MORE[lang](remaining)}` : list;
}

/**
 * Markdown bullet list; Outlook needs each list item to end with \r.
 */
function formatOrgBullets(orgLabels: string[], max: number, lang: Lang): string {
  const items = orgLabels.slice(0, max).map((label) => `- ${label}`);
  const remaining = orgLabels.length - items.length;
  if (remaining > 0) {
    items.push(`- ${MORE[lang](remaining)}`);
  }
  return items.map((item) => `${item}\r`).join("");
}

function fillTemplate(template: string, decision: Decision, lang: Lang, maxBullets: number): string {
  return template
    .replace(/\{count\}/g, String(decision.orgCount))
    .replace(/\{orgBullets\}/g, formatOrgBullets(decision.orgLabels, maxBullets, lang))
    .replace(/\{orgList\}/g, formatOrgList(decision.orgLabels, lang))
    .replace(/\{label\}/g, decision.labelName ?? "")
    .replace(/\{detail\}/g, decision.detail ?? "");
}

/**
 * Plain-text version for clients without markdown support (before Mailbox 1.15).
 */
function toPlain(markdown: string): string {
  return markdown
    .replace(/\*\*/g, "")
    .replace(/^- /gm, "\u2022 ")
    .replace(/\r/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

/**
 * Builds the warning for the given decision/policy/language (§7.2, FR-12/FR-15). Templates are written in the
 * markdown subset Outlook supports (bold, bullet lists, blank lines); the plain text is derived from them.
 */
export function buildMessage(decision: Decision, policy: Policy, lang: Lang): BuiltMessage {
  const set = policy.messages[lang];
  const templates: Record<Exclude<DecisionLevel, "none">, string> = {
    blockedLabel: set.labelBlocked,
    blockedUnlabeled: set.unlabeledBlocked,
    blockedCustomerLabel: set.customerLabelBlocked,
    lookalikeDomain: set.lookalikeDomain,
    brandInDomain: set.brandInDomain,
    crossCustomer: set.crossCustomer,
    contentMismatch: set.contentMismatch,
    tooManyExternal: set.tooManyExternal,
    standard: set.standard,
  };
  const template = templates[decision.level === "none" ? "standard" : decision.level];
  let markdown = "";
  for (const maxBullets of BULLET_LIMITS) {
    markdown = fillTemplate(template, decision, lang, maxBullets);
    if (markdown.length <= MAX_DIALOG_CHARS) {
      break;
    }
  }
  if (markdown.length > MAX_DIALOG_CHARS) {
    markdown = `${markdown.slice(0, MAX_DIALOG_CHARS - 1)}\u2026`;
  }
  return {
    plain: toPlain(markdown),
    markdown,
    cancelLabel: set.cancelLabel,
  };
}
